import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, DoorOpen, Loader2, X, Trash2 } from "lucide-react";
import { householdsApi, formatError } from "../lib/api";
import { toast } from "sonner";

/**
 * LeaveHouseholdDialog — shown when the user taps "Leave household".
 *
 * Two faces:
 *   • Standard leave (member_count > 1): short confirmation, button reads
 *     "Leave household".
 *   • LAST-member leave (member_count <= 1): destructive warning with
 *     container + location counts. Button reads "Delete & leave" and is red.
 *
 * Stats are fetched lazily when the dialog opens so we always show the
 * latest numbers (vs caching them stale from earlier renders).
 */
export const LeaveHouseholdDialog = ({ open, onClose, householdName, onConfirmed }) => {
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setStats(null);
      setStatsError(false);
      return;
    }
    let cancelled = false;
    setStatsError(false);
    householdsApi
      .stats()
      .then((s) => { if (!cancelled) setStats(s); })
      .catch(() => {
        // Don't silently default to the non-destructive flow — refusing
        // the confirm button is safer than letting the user delete data
        // without an accurate warning.
        if (!cancelled) setStatsError(true);
      });
    return () => { cancelled = true; };
  }, [open]);

  const handleLeave = async () => {
    setBusy(true);
    try {
      const r = await householdsApi.leave();
      toast.success("You left the household");
      onConfirmed?.(r);
      onClose?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const isLast = stats?.is_last_member;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => !busy && onClose?.()}
          className="fixed inset-0 z-[2147483600] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center"
          data-testid="leave-dialog-overlay"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[440px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4"
            data-testid="leave-dialog"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                isLast ? "bg-[#FBEAE5] text-[#9C3E1D]" : "bg-[#FAF1D7] text-[#8A6A1A]"
              }`}>
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                  {isLast ? "Permanent deletion" : "Leave household"}
                </div>
                <h2 className="font-heading text-lg font-semibold text-[#2D3142] mt-0.5">
                  {isLast
                    ? `You're the last member of "${householdName}"`
                    : `Leave "${householdName}"?`}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => !busy && onClose?.()}
                data-testid="leave-dialog-close"
                disabled={busy}
                className="w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {!stats && !statsError ? (
              <div className="py-6 text-center text-sm text-[#8A93A6]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Checking…
              </div>
            ) : statsError ? (
              <div className="py-4 text-sm text-[#9C3E1D] bg-[#FBEAE5] rounded-xl px-3"
                   data-testid="leave-dialog-stats-error">
                Couldn't check household status. Close this dialog and try again — we won't
                let you leave until we can confirm the impact.
              </div>
            ) : isLast ? (
              <div data-testid="leave-dialog-destructive-body">
                <p className="text-sm text-[#4F5D75] leading-relaxed">
                  Leaving will <strong className="text-[#9C3E1D]">permanently delete</strong>:
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-[#2D3142]">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F]" />
                    <strong>{stats.container_count}</strong>
                    <span className="text-[#4F5D75]">
                      container{stats.container_count === 1 ? "" : "s"} (all their data, history, and shares)
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F]" />
                    <strong>{stats.location_count}</strong>
                    <span className="text-[#4F5D75]">
                      storage location{stats.location_count === 1 ? "" : "s"}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F]" />
                    <span className="text-[#4F5D75]">The household itself (the join code is freed)</span>
                  </li>
                </ul>
                <p className="text-xs text-[#8A93A6] mt-3 leading-relaxed">
                  This cannot be undone. Consider exporting or transferring ownership first.
                </p>
              </div>
            ) : (
              <div data-testid="leave-dialog-standard-body">
                <p className="text-sm text-[#4F5D75] leading-relaxed">
                  You'll lose access to its <strong>{stats.container_count}</strong>{" "}
                  container{stats.container_count === 1 ? "" : "s"}. Other members keep
                  everything — and the owner(s) can re-invite you any time with the join code.
                </p>
              </div>
            )}

            <div className="mt-5 flex gap-2.5">
              <button
                type="button"
                onClick={() => !busy && onClose?.()}
                disabled={busy}
                data-testid="leave-dialog-cancel"
                className="flex-1 h-12 rounded-xl bg-white border border-[#E2E0D8] text-[#2D3142] font-semibold hover:bg-[#F5F3ED]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLeave}
                disabled={busy || !stats || statsError}
                data-testid="leave-dialog-confirm"
                className={`flex-1 h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 ${
                  isLast
                    ? "bg-[#C04020] hover:bg-[#9C3E1D]"
                    : "bg-[#E07A5F] hover:bg-[#C9603E]"
                }`}
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isLast ? (
                  <Trash2 className="w-4 h-4" />
                ) : (
                  <DoorOpen className="w-4 h-4" />
                )}
                {isLast ? "Delete & leave" : "Leave household"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeaveHouseholdDialog;
